/**
 * 캘린더 뷰 시스템 (spec 2026-09-18 §9): 뷰 탭(클릭·이름·순서·＋·⋯ 메뉴), 조건 툴바
 * (필터·정렬·그룹·열·숨김 칩 + 팝오버), 저장/되돌리기. 상태의 진실은 URL 이다 —
 * 모든 변경은 축(아래 AXES — flt m s g c hid dn rng)을 URL 에 적고 navigate() 로 서버가 다시
 * 그린다. 리전(viewtabs/viewbar)이 SPA 교체마다 갈리므로 리스너는 전부 문서에 위임한다.
 */
import { navigate } from './calendar-spa.js';
import { showContextMenu } from './context-menu.js';
import { appConfirm } from './confirm-dialog.js';
import { openExclusive, closedExclusive } from './popover-registry.js';
import { showToast, CLIENT_ID } from './calendar-history.js';
import { openPeoplePicker } from './people-picker.js';
import { openEventPicker } from './event-picker.js';
import { attachDatePicker, closeDatePicker, syncDateLabel } from './datepicker.js';
import { FIELD_ICON_PATHS, FIELD_KEY_ICON, iconSVG } from './field-icons.js';

// 뷰 축 한 곳 (백로그 BD6): 설정 JSON(c) → 상태 조각(read), 저장값과 다른 축만 URL 에 적기(write).
// 순서가 URL 순서이고 서버의 viewAxes(calendar_views.go)와 같다 — 축을 더하려면 두 목록에 한 줄씩.
const toggleAxis = (key) => ({ keys: [key], params: [key], read: (c) => ({ [key]: !!c[key] }), write: (u, cfg) => u.searchParams.set(key, cfg[key] ? 'on' : 'off') });
const AXES = [
    { keys: ['filters', 'match'], params: ['flt', 'm'],
        read: (c) => ({ filters: (c.filters || []).map((f) => ({ f: f.f, op: f.op, v: f.v || '' })), match: c.match || '' }),
        write: (u, cfg) => {
            if (!cfg.filters.length) u.searchParams.append('flt', '');
            cfg.filters.forEach((f) => u.searchParams.append('flt', f.f + '~' + f.op + '~' + (f.v || '')));
            if (cfg.match === 'any') u.searchParams.set('m', 'any');
        } },
    { keys: ['sort'], params: ['s'],
        read: (c) => ({ sort: (c.sort || []).map((s) => ({ k: s.k, desc: !!s.desc })) }),
        write: (u, cfg) => {
            if (!cfg.sort.length) u.searchParams.append('s', '');
            cfg.sort.forEach((s) => u.searchParams.append('s', s.k + (s.desc ? '~d' : '')));
        } },
    { keys: ['group'], params: ['g'], read: (c) => ({ group: c.group ? c.group.f : '' }), write: (u, cfg) => u.searchParams.set('g', cfg.group || '') },
    { keys: ['cols'], params: ['c'], read: (c) => ({ cols: (c.cols || []).slice() }), write: (u, cfg) => u.searchParams.set('c', cfg.cols.join(',')) },
    toggleAxis('hid'),
    toggleAxis('dn'),
    { keys: ['rng'], params: ['rng'], read: (c) => ({ rng: c.rng || '' }), write: (u, cfg) => u.searchParams.set('rng', cfg.rng || 'month') },
];
const AXIS_PARAMS = AXES.reduce((out, a) => out.concat(a.params), []);
const readCfg = (c) => AXES.reduce((out, a) => Object.assign(out, a.read(c || {})), {});

const POP_ID = 'cal-view-pop';

function data() {
    const el = document.getElementById('calViewData');
    if (!el) return null;
    try { return JSON.parse(el.textContent || '{}'); } catch (_) { return null; }
}
function csrf() {
    if (window.getCSRFToken) return window.getCSRFToken();
    const m = document.querySelector('meta[name="csrf-token"]');
    return m ? m.content : '';
}
function post(url, body) {
    // X-Cal-Client: 뷰 변경 SSE(AQ7)의 자기 에코를 이 탭이 거른다 — 이 탭은 저장 뒤 스스로 이동한다
    return fetch(url, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf(), Accept: 'application/json', 'X-Cal-Client': CLIENT_ID }, body: JSON.stringify(body || {}) })
        .then(async (r) => { const j = await r.json().catch(() => ({})); if (!r.ok) throw new Error(j.message || String(r.status)); return j; });
}
function cfgNow() {
    const d = data();
    return readCfg(d && d.cfg);
}
// 저장된 설정을 cfgNow() 와 같은 모양으로
function savedCfg() {
    const d = data();
    return readCfg(d && d.view && d.view.config);
}
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
// URL ← 설정: 저장값과 다른 축만 적는다 (한 축이라도 URL 에 있으면 그 축은 URL 이 저장값을 덮으므로,
// 바꾸지 않은 축을 적어 두면 — 예: 열 — 나중에 생긴 속성 열이 그 뷰에 나타나지 않는다).
function applyCfg(cfg) {
    const u = new URL(window.location.href);
    AXIS_PARAMS.forEach((k) => u.searchParams.delete(k));
    const saved = savedCfg();
    AXES.forEach((ax) => { if (ax.keys.some((k) => !same(cfg[k], saved[k]))) ax.write(u, cfg); });
    return navigate(u.pathname + '?' + u.searchParams.toString(), { keepScroll: true });
}
function cleanURL(viewId) {
    const u = new URL(window.location.href);
    AXIS_PARAMS.forEach((k) => u.searchParams.delete(k));
    ['view', 'sort', 'pf', 'pv', 'st'].forEach((k) => u.searchParams.delete(k));
    if (viewId) u.searchParams.set('v', viewId);
    return u.pathname + '?' + u.searchParams.toString();
}
function L(key, fallback) { const d = data(); return (d && d.l && d.l[key]) || fallback; }
function fieldOf(key) { const d = data(); return d ? (d.fields || []).find((f) => f.key === key) : null; }
// 값 유형: 계산 속성(롤업·수식·자동 번호)은 결과 유형으로 연산·값 입력을 고른다 (수식 spec §10)
const vt = (f) => f.vtype || f.type;
function defaultOp(f) {
    const t = vt(f);
    const want = t === 'text' || t === 'url' ? 'contains' : t === 'number' ? 'eq' : t === 'date' ? 'on' : t === 'multiselect' || t === 'list' ? 'has' : 'is';
    return !f.ops || !f.ops.length || f.ops.includes(want) ? want : f.ops[0];
}

// ── 팝오버 ──
let pop = null;
function closePop() { if (pop) { pop.remove(); pop = null; closedExclusive(POP_ID); } }
function openPop(anchor, build) {
    closePop();
    pop = document.createElement('div');
    pop.className = 'cal-vpop';
    build(pop);
    document.body.appendChild(pop);
    const r = anchor.getBoundingClientRect();
    pop.style.left = Math.max(8, Math.min(r.left, window.innerWidth - pop.offsetWidth - 8)) + 'px';
    pop.style.top = Math.max(8, Math.min(r.bottom + 4, window.innerHeight - pop.offsetHeight - 8)) + 'px';
    openExclusive(POP_ID, () => { if (pop) { pop.remove(); pop = null; } }, { roots: () => [pop, document.querySelector('.pp-pop'), document.querySelector('.att-dp-pop')] });
    const first = pop.querySelector('input, select, button');
    if (first) first.focus();
}
function el(tag, cls, text) { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; }
function btn(cls, text, onClick) { const b = el('button', cls, text); b.type = 'button'; b.addEventListener('click', onClick); return b; }

// 필터 칩 편집: 연산 select + 유형별 값 컨트롤 + 제거/적용
function editFilter(anchor, index) {
    const d = data(); if (!d) return;
    const cfg = cfgNow();
    const flt = cfg.filters[index]; if (!flt) return;
    const f = fieldOf(flt.f); if (!f) return;
    openPop(anchor, (box) => {
        box.appendChild(el('div', 'cal-vpop-title', f.label));
        const opSel = el('select');
        f.ops.forEach((op) => { const o = el('option', '', d.ops[op] || op); o.value = op; opSel.appendChild(o); });
        opSel.value = flt.op;
        box.appendChild(opSel);
        const val = el('div', 'cal-vpop-val');
        box.appendChild(val);
        let value = flt.v || '';
        function apply() { cfg.filters[index] = { f: flt.f, op: opSel.value, v: value }; closePop(); applyCfg(cfg); }
        function renderVal() {
            val.innerHTML = '';
            const op = opSel.value;
            if (op === 'empty' || op === 'not_empty') return;
            const multi = op === 'in' || op === 'not_in' || op === 'has_any';
            if (vt(f) === 'date') {
                const sel = el('select');
                d.tokens.forEach((t) => { const o = el('option', '', t.label); o.value = t.key; sel.appendChild(o); });
                const custom = el('option', '', L('pick_date', '날짜 지정')); custom.value = '__date'; sel.appendChild(custom);
                const isTok = d.tokens.some((t) => t.key === value);
                sel.value = isTok ? value : '__date';
                const d1 = el('input'); d1.type = 'date'; const d2 = el('input'); d2.type = 'date';
                const parts = isTok ? ['', ''] : value.split('|');
                d1.value = parts[0] || ''; d2.value = parts[1] || '';
                const sync = () => { if (sel.value !== '__date') { value = sel.value; d1.hidden = true; d2.hidden = true; return; } d1.hidden = false; d2.hidden = op !== 'between'; value = op === 'between' ? d1.value + '|' + d2.value : d1.value; };
                sel.addEventListener('change', sync); d1.addEventListener('change', sync); d2.addEventListener('change', sync);
                val.appendChild(sel); val.appendChild(d1); val.appendChild(d2); sync();
                return;
            }
            if (vt(f) === 'text' || vt(f) === 'url' || vt(f) === 'number' || vt(f) === 'list') {
                // list(수식·롤업의 목록 결과): 값 하나를 적는다 — 여럿은 | 로
                const inp = el('input'); inp.type = vt(f) === 'number' ? 'number' : 'text'; inp.value = value;
                inp.addEventListener('input', () => { value = inp.value; });
                inp.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); apply(); } });
                val.appendChild(inp); return;
            }
            if (vt(f) === 'check') {
                const list = el('div', 'cal-vpop-list');
                [['1', L('yes', '예')], ['', L('no', '아니오')]].forEach(([v, label]) => { const lab = el('label'); const r = el('input'); r.type = 'radio'; r.name = 'v'; r.checked = value === v; r.addEventListener('change', () => { value = v; }); lab.appendChild(r); lab.appendChild(document.createTextNode(label)); list.appendChild(lab); });
                val.appendChild(list); return;
            }
            if (f.vtype === 'relation') {
                // 관계: 대상 범위의 일정을 피커로 고른다 (속성 spec §11)
                const chosen = value.split('|').filter(Boolean).map((id) => ({ id, title: id }));
                const pick = btn('btn btn-secondary btn-sm', L('pick_events', '일정 선택…'), () => {
                    openEventPicker(pick, { scope: f.target === 'me' || !f.target ? 'me' : f.target, multi, values: chosen, labels: { search: L('pick_events', '일정 선택…') }, onChange: (list) => { chosen.splice(0, chosen.length, ...list); value = list.map((x) => x.id).join('|'); } });
                });
                val.appendChild(pick); return;
            }
            if (f.type === 'user' || f.vtype === 'user' || f.vtype === 'users') {
                const chosen = new Set(value.split('|').filter(Boolean));
                const me = el('label'); const mi = el('input'); mi.type = 'checkbox'; mi.checked = chosen.has('me');
                mi.addEventListener('change', () => { if (mi.checked) chosen.add('me'); else chosen.delete('me'); value = Array.from(chosen).join('|'); });
                me.appendChild(mi); me.appendChild(document.createTextNode(L('me', '나')));
                const pick = btn('btn btn-secondary btn-sm', L('pick_people', '사람 선택…'), () => {
                    openPeoplePicker(pick, { multi, allowMe: false, values: Array.from(chosen).filter((x) => x !== 'me'), onChange: (ids) => { const keepMe = chosen.has('me'); chosen.clear(); if (keepMe) chosen.add('me'); (Array.isArray(ids) ? ids : [ids]).filter(Boolean).forEach((id) => chosen.add(id)); value = Array.from(chosen).join('|'); } });
                });
                val.appendChild(me); val.appendChild(pick); return;
            }
            // select · multiselect · status · color · calendar · share_*
            const list = el('div', 'cal-vpop-list');
            const chosen = new Set(value.split('|').filter(Boolean));
            let options = f.options || [];
            if (f.type === 'status' || f.vtype === 'status') {
                // 상태: 그룹 머리글(할 일 → 진행 중 → 완료) 아래 옵션
                const rank = { todo: 0, doing: 1, done: 2 };
                options = options.slice().sort((a, b) => (rank[a.group || 'todo'] || 0) - (rank[b.group || 'todo'] || 0));
                let lastGroup = null;
                options = options.map((o) => Object.assign({}, o, { _head: (o.group || 'todo') !== lastGroup ? (lastGroup = (o.group || 'todo')) : '' }));
            }
            options.forEach((o) => {
                if (o._head) list.appendChild(el('div', 'cal-vpop-group', L('group_' + o._head, o._head)));
                const lab = el('label'); const c = el('input'); c.type = multi ? 'checkbox' : 'radio'; c.name = 'v'; c.checked = chosen.has(o.value);
                c.addEventListener('change', () => { if (!multi) chosen.clear(); if (c.checked) chosen.add(o.value); else chosen.delete(o.value); value = Array.from(chosen).join('|'); });
                lab.appendChild(c);
                if (o.color) lab.appendChild(el('span', 'prop-pill ' + o.color, o.label)); else lab.appendChild(document.createTextNode(o.label));
                list.appendChild(lab);
            });
            val.appendChild(list);
        }
        opSel.addEventListener('change', renderVal);
        renderVal();
        const foot = el('div', 'cal-vpop-foot');
        foot.appendChild(btn('btn btn-ghost btn-sm danger', L('remove', '제거'), () => { cfg.filters.splice(index, 1); closePop(); applyCfg(cfg); }));
        foot.appendChild(btn('btn btn-primary btn-sm', L('apply', '적용'), apply));
        box.appendChild(foot);
    });
}

// 필터·정렬·그룹 메뉴 항목 앞의 필드 아이콘 (툴바 2차 (c) — 「메뉴가 글자뿐」; 표는 field-icons.js).
// 키 → 아이콘: 내장 키가 먼저(「상태」·「캘린더」는 내장에서 select 지만 제 모양이 있다), 속성은 그 유형
function fieldIcon(key) {
    if (FIELD_KEY_ICON[key]) return iconSVG(FIELD_KEY_ICON[key]);
    const f = fieldOf(key);
    return iconSVG(f && FIELD_ICON_PATHS[f.type] ? f.type : 'text');
}
function menuItems(list, onPick) { return list.map((x) => ({ label: x.label, icon: x.key ? fieldIcon(x.key) : undefined, onClick: () => onPick(x) })); }
// 내장 필드 ↔ 속성 사이 구분선 (AY8): 같은 이름(「상태」)이어도 어느 쪽인지 보이게 — 속성 라벨에는 서버가 「(속성)」도 붙인다
function fieldItems(list, onPick) {
    const out = menuItems(list, onPick);
    const i = list.findIndex((x) => String(x.key || '').startsWith('prop:'));
    if (i > 0) out.splice(i, 0, { separator: true });
    return out;
}

// 기간 팝오버 (AQ5): 사용자 지정 날짜 둘 → rng=from|to. 날짜 칸은 앱의 인앱 데이트피커(BD5 — 네이티브
// <input type=date> 는 로케일마다 모양이 달랐다). 시작을 고르면 끝이 비었거나 더 이를 때 끝을 따라오게 한다.
function editRange(anchor) {
    const cfg = cfgNow();
    const cur = (cfg.rng || '').split('|');
    const ph = L('pick_date', '날짜 지정');
    openPop(anchor, (box) => {
        box.classList.add('cal-vpop-range');
        const field = (label, val) => {
            const row = el('div', 'cal-vpop-row');
            row.appendChild(el('span', 'cal-vpop-rlabel text-muted', label));
            const wrap = el('div', 'att-dp');
            const b = el('button', 'form-input att-dp-btn'); b.type = 'button';
            b.appendChild(el('span', 'att-dp-label'));
            const hidden = el('input'); hidden.type = 'hidden'; hidden.value = val;
            wrap.appendChild(b); wrap.appendChild(hidden); // 버튼이 먼저 — openPop 이 첫 컨트롤에 포커스를 준다
            row.appendChild(wrap);
            box.appendChild(row);
            syncDateLabel(b, hidden, ph);
            return { b, hidden };
        };
        const from = field(L('range_from', '시작'), cur.length === 2 ? cur[0] : '');
        const to = field(L('range_to', '끝'), cur.length === 2 ? cur[1] : '');
        const ok = el('button', 'btn btn-primary btn-sm', L('range_apply', '적용')); ok.type = 'button';
        const sync = () => { ok.disabled = !(from.hidden.value && to.hidden.value); };
        const set = (f, v) => { f.hidden.value = v; syncDateLabel(f.b, f.hidden, ph); };
        const weekStart = (document.getElementById('calendarModal') || { dataset: {} }).dataset.weekStart; // 주 시작 요일 (XA21)
        attachDatePicker(from.b, from.hidden, { placeholder: ph, weekStart, onSet: (v) => { if (v && (!to.hidden.value || to.hidden.value < v)) set(to, v); sync(); } });
        attachDatePicker(to.b, to.hidden, { placeholder: ph, weekStart, onSet: (v) => { if (v && from.hidden.value > v) set(from, v); sync(); } });
        ok.addEventListener('click', () => { if (!from.hidden.value || !to.hidden.value) return; closeDatePicker(); cfg.rng = from.hidden.value + '|' + to.hidden.value; closePop(); applyCfg(cfg); });
        const foot = el('div', 'cal-vpop-foot'); foot.appendChild(ok);
        box.appendChild(foot);
        sync();
    });
}

// 열 팝오버: 체크 + 끌어서 순서
function editCols(anchor) {
    const d = data(); if (!d) return;
    const cfg = cfgNow();
    openPop(anchor, (box) => {
        box.appendChild(el('div', 'cal-vpop-hint text-muted', L('cols_hint', '체크로 표시 · 끌어서 순서')));
        const list = el('div', 'cal-vpop-list');
        const order = cfg.cols.concat(d.cols.map((c) => c.key).filter((k) => !cfg.cols.includes(k)));
        const overlay = d.scope && d.scope.overlay && (d.scopes || []).length;
        const scopeOf = (key) => { const m = d.cols.find((c) => c.key === key); return (m && m.scope) || ''; };
        // 오버레이: 내장 열 → 범위(내 속성 · 프로젝트별)마다 머리글 + 그 범위의 열 (속성 spec §8)
        const groups = overlay ? [{ key: '', label: '' }].concat(d.scopes) : [{ key: null, label: '' }];
        groups.forEach((g) => {
            const keys = g.key === null ? order : order.filter((k) => scopeOf(k) === g.key);
            if (!keys.length) return;
            if (g.label) list.appendChild(el('div', 'cal-vpop-group', g.label));
            keys.forEach((key) => {
                const meta = d.cols.find((c) => c.key === key); if (!meta) return;
                const lab = el('label'); lab.draggable = true; lab.dataset.key = key;
                const c = el('input'); c.type = 'checkbox'; c.checked = cfg.cols.includes(key);
                lab.appendChild(el('span', 'cal-vpop-grip', '⋮⋮')); lab.appendChild(c); lab.appendChild(document.createTextNode(meta.label));
                list.appendChild(lab);
            });
        });
        let drag = null;
        list.addEventListener('dragstart', (e) => { drag = e.target.closest('label'); if (drag) drag.classList.add('dragging'); });
        list.addEventListener('dragend', () => { if (drag) drag.classList.remove('dragging'); drag = null; });
        list.addEventListener('dragover', (e) => { if (!drag) return; e.preventDefault(); const over = e.target.closest('label'); if (!over || over === drag) return; const r = over.getBoundingClientRect(); if (e.clientY < r.top + r.height / 2) list.insertBefore(drag, over); else over.insertAdjacentElement('afterend', drag); });
        box.appendChild(list);
        const foot = el('div', 'cal-vpop-foot');
        if (document.getElementById('calpPropsBtn')) foot.appendChild(btn('btn btn-ghost btn-sm', '＋ ' + L('new_prop', '새 속성'), () => { closePop(); const b = document.getElementById('calpPropsBtn'); if (b) b.click(); }));
        foot.appendChild(btn('btn btn-primary btn-sm', L('apply', '적용'), () => { cfg.cols = Array.from(list.querySelectorAll('label')).filter((l) => l.querySelector('input').checked).map((l) => l.dataset.key); closePop(); applyCfg(cfg); }));
        box.appendChild(foot);
    });
}

// 탭 이름 인라인 편집
function renameTab(tab) {
    const name = tab.querySelector('.cal-vtab-name'); if (!name || name.querySelector('input')) return;
    const old = name.textContent;
    const inp = el('input'); inp.value = old; inp.maxLength = 40; inp.setAttribute('aria-label', L('view_name', '뷰 이름'));
    name.textContent = ''; name.appendChild(inp); inp.focus(); inp.select();
    let done = false;
    const finish = (save) => {
        if (done) return; done = true;
        const v = inp.value.trim();
        name.textContent = save && v ? v : old;
        if (!save || !v || v === old) return;
        post('/calendar/views/' + encodeURIComponent(tab.dataset.id) + '/update', { name: v }).catch((err) => { name.textContent = old; showToast(err.message || L('failed', '저장하지 못했습니다'), { error: true }); });
    };
    inp.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); finish(true); } else if (e.key === 'Escape') { e.preventDefault(); finish(false); } });
    inp.addEventListener('blur', () => finish(true));
    inp.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); });
}
function tabsBase() { const t = document.getElementById('calViewTabs'); return (t && t.dataset.base) || window.location.pathname; }
function createURL(d) { return d.scope.calendarId ? '/calendar/calendars/' + encodeURIComponent(d.scope.calendarId) + '/views' : '/calendar/views'; }
function tabMenu(tab, x, y) {
    const d = data(); if (!d) return;
    const id = tab.dataset.id;
    const base = tabsBase();
    const view = (d.views || []).find((v) => v.id === id) || { name: tab.textContent.trim(), kind: tab.dataset.kind };
    const fail = (err) => showToast(err.message || L('failed', '저장하지 못했습니다'), { error: true });
    const items = [
        { label: L('rename', '이름 바꾸기'), onClick: () => renameTab(tab) },
        { label: L('duplicate', '복제'), onClick: () => post(createURL(d), { kind: view.kind, name: view.name + ' 2', config: id === d.view.id ? d.view.config : undefined }).then((v) => navigate(base + '?v=' + v.id)).catch(fail) },
        { label: L('change_kind', '종류 바꾸기'), children: (d.kinds || []).map((k) => ({ label: k.label, onClick: () => post('/calendar/views/' + encodeURIComponent(id) + '/update', { kind: k.key }).then(() => navigate(base + '?v=' + id)).catch(fail) })) },
        { label: L('set_default', '기본으로'), disabled: !!view.default, onClick: () => post('/calendar/views/' + encodeURIComponent(id) + '/default', {}).then(() => navigate(window.location.pathname + window.location.search, { replace: true, keepScroll: true })).catch(fail) },
        { separator: true },
        { label: L('delete_view', '뷰 삭제'), danger: true, disabled: (d.views || []).length <= 1, onClick: () => appConfirm({ message: L('delete_confirm', '뷰 "%s"를 삭제할까요?').replace('%s', view.name), danger: true }).then((ok) => { if (!ok) return null; return post('/calendar/views/' + encodeURIComponent(id) + '/delete', {}).then((r) => navigate(base + '?v=' + (r.next || ''))); }).catch(fail) },
    ];
    showContextMenu(x, y, items);
}
function addViewMenu(anchor) {
    const d = data(); if (!d) return;
    const r = anchor.getBoundingClientRect();
    const base = tabsBase();
    showContextMenu(r.left, r.bottom + 4, (d.kinds || []).map((k) => ({ label: k.label, onClick: () => {
        post(createURL(d), { kind: k.key }).then((v) => { try { sessionStorage.setItem('calViewRename', v.id); } catch (_) { /* ignore */ } return navigate(base + '?v=' + v.id); }).catch((err) => showToast(err.message || L('failed', '저장하지 못했습니다'), { error: true }));
    } })));
}

// 탭 드래그 순서
let dragTab = null;
function tabsReorder() {
    const d = data(); if (!d) return;
    const ids = Array.from(document.querySelectorAll('#calViewTabs .cal-vtab')).map((t) => t.dataset.id);
    const url = d.scope.calendarId ? '/calendar/calendars/' + encodeURIComponent(d.scope.calendarId) + '/views/reorder' : '/calendar/views/reorder';
    post(url, { ids }).catch((err) => showToast(err.message || L('failed', '저장하지 못했습니다'), { error: true }));
}
function canEditTabs() { const t = document.getElementById('calViewTabs'); return !!(t && t.dataset.canEdit); }

export function setupCalendarViews() {
    if (document.documentElement.dataset.calViewsBound) return;
    document.documentElement.dataset.calViewsBound = '1';

    document.addEventListener('click', (e) => {
        const add = e.target.closest('#calViewAdd');
        if (add) { e.preventDefault(); addViewMenu(add); return; }
        const save = e.target.closest('#calViewSave');
        if (save) {
            const d = data(); if (!d) return;
            save.disabled = true;
            post('/calendar/views/' + encodeURIComponent(d.view.id) + '/update', { config: d.cfg }).then(() => navigate(cleanURL(d.view.id), { keepScroll: true })).catch((err) => { save.disabled = false; showToast(err.message || L('failed', '저장하지 못했습니다'), { error: true }); });
            return;
        }
        const revert = e.target.closest('#calViewRevert');
        if (revert) { const d = data(); navigate(cleanURL(d && d.view.id), { keepScroll: true }); return; }
        const x = e.target.closest('[data-vchip-x]');
        if (x) {
            e.preventDefault(); e.stopPropagation();
            const chip = x.closest('.cal-vchip'); const cfg = cfgNow(); const i = Number(chip.dataset.index);
            if (chip.dataset.vchip === 'filter') cfg.filters.splice(i, 1); else cfg.sort.splice(i, 1);
            applyCfg(cfg); return;
        }
        const chip = e.target.closest('.cal-vchip');
        if (chip) {
            e.preventDefault();
            if (chip.dataset.vchip === 'filter') editFilter(chip, Number(chip.dataset.index));
            else { const cfg = cfgNow(); const s = cfg.sort[Number(chip.dataset.index)]; if (s) { s.desc = !s.desc; applyCfg(cfg); } }
            return;
        }
        const act = e.target.closest('[data-vact]');
        if (act) {
            e.preventDefault();
            const d = data(); if (!d) return;
            const r = act.getBoundingClientRect();
            const cfg = cfgNow();
            switch (act.dataset.vact) {
                case 'filter':
                    showContextMenu(r.left, r.bottom + 4, fieldItems(d.fields, (f) => { cfg.filters.push({ f: f.key, op: defaultOp(f), v: '' }); applyCfg(cfg).then(() => { const chips = document.querySelectorAll('.cal-vchip[data-vchip="filter"]'); const last = chips[chips.length - 1]; if (last) editFilter(last, Number(last.dataset.index)); }); }));
                    break;
                case 'match':
                    showContextMenu(r.left, r.bottom + 4, [{ label: L('match_all', '모든 조건'), onClick: () => { cfg.match = ''; applyCfg(cfg); } }, { label: L('match_any', '하나라도'), onClick: () => { cfg.match = 'any'; applyCfg(cfg); } }]);
                    break;
                case 'sort':
                    showContextMenu(r.left, r.bottom + 4, fieldItems(d.sortKeys.filter((k) => !cfg.sort.some((s) => s.k === k.key)), (k) => { cfg.sort.push({ k: k.key, desc: false }); applyCfg(cfg); }));
                    break;
                case 'group':
                    showContextMenu(r.left, r.bottom + 4, [{ label: L('none_group', '그룹 없음'), icon: iconSVG('none'), onClick: () => { cfg.group = ''; applyCfg(cfg); } }, { separator: true }].concat(fieldItems(d.fields.filter((f) => f.group), (f) => { cfg.group = f.key; applyCfg(cfg); })));
                    break;
                case 'cols':
                    editCols(act);
                    break;
                case 'hid':
                    cfg.hid = !cfg.hid; applyCfg(cfg);
                    break;
                case 'dn':
                    cfg.dn = !cfg.dn; applyCfg(cfg);
                    break;
                case 'range':
                    showContextMenu(r.left, r.bottom + 4, [
                        { label: L('range_month', '이 달'), onClick: () => { cfg.rng = ''; applyCfg(cfg); } },
                        { label: L('range_quarter', '이번 분기'), onClick: () => { cfg.rng = 'quarter'; applyCfg(cfg); } },
                        { label: L('range_year', '올해'), onClick: () => { cfg.rng = 'year'; applyCfg(cfg); } },
                        { label: L('range_all', '전체'), onClick: () => { cfg.rng = 'all'; applyCfg(cfg); } },
                        { separator: true },
                        { label: L('range_custom', '사용자 지정…'), onClick: () => editRange(act) },
                    ]);
                    break;
                default:
            }
            return;
        }
        // 표 헤더 클릭 = 그 열로 정렬 토글 (첫 키가 같으면 방향 반전)
        const th = e.target.closest('[data-sort-key]');
        if (th && th.closest('#projHub, #calList')) {
            e.preventDefault();
            const cfg = cfgNow(); const key = th.dataset.sortKey === 'period' ? 'start' : th.dataset.sortKey;
            if (cfg.sort.length && cfg.sort[0].k === key) cfg.sort[0].desc = !cfg.sort[0].desc; else cfg.sort = [{ k: key, desc: false }];
            applyCfg(cfg); return;
        }
        // 표의 그룹 접기
        const fold = e.target.closest('[data-group-fold]');
        if (fold) {
            const head = fold.closest('.cal-group-head'); const root = head.closest('#projHub, #calList'); const key = head.dataset.group;
            const folded = head.dataset.folded !== '1';
            head.dataset.folded = folded ? '1' : ''; fold.setAttribute('aria-expanded', folded ? 'false' : 'true');
            if (root) root.querySelectorAll('[data-group][data-id]').forEach((row) => { if (row.dataset.group === key) row.hidden = folded; });
            try { const k = 'calViewFold:' + ((root && root.dataset.viewId) || ''); const set = new Set(JSON.parse(localStorage.getItem(k) || '[]')); if (folded) set.add(key); else set.delete(key); localStorage.setItem(k, JSON.stringify(Array.from(set))); } catch (_) { /* ignore */ }
        }
    });
    document.addEventListener('contextmenu', (e) => {
        const tab = e.target.closest('#calViewTabs .cal-vtab');
        if (!tab || !canEditTabs()) return;
        e.preventDefault(); tabMenu(tab, e.clientX, e.clientY);
    });
    document.addEventListener('dblclick', (e) => { const tab = e.target.closest('#calViewTabs .cal-vtab'); if (tab && canEditTabs()) { e.preventDefault(); renameTab(tab); } });
    document.addEventListener('dragstart', (e) => { const tab = e.target.closest && e.target.closest('#calViewTabs .cal-vtab'); if (!tab || !canEditTabs()) return; dragTab = tab; tab.classList.add('dragging'); e.dataTransfer.effectAllowed = 'move'; });
    document.addEventListener('dragover', (e) => { if (!dragTab) return; const over = e.target.closest && e.target.closest('#calViewTabs .cal-vtab'); if (!over || over === dragTab) return; e.preventDefault(); const r = over.getBoundingClientRect(); if (e.clientX < r.left + r.width / 2) over.parentNode.insertBefore(dragTab, over); else over.insertAdjacentElement('afterend', dragTab); });
    document.addEventListener('dragend', () => { if (!dragTab) return; dragTab.classList.remove('dragging'); const tabs = dragTab.closest('#calViewTabs'); dragTab = null; if (tabs) tabsReorder(); });
    // 새로 만든 뷰: 도착하면 이름 편집 상태로
    const pendingRename = () => {
        let id = ''; try { id = sessionStorage.getItem('calViewRename') || ''; } catch (_) { return; }
        if (!id) return;
        const tab = document.querySelector('#calViewTabs .cal-vtab.active[data-id="' + CSS.escape(id) + '"]');
        if (!tab) return;
        try { sessionStorage.removeItem('calViewRename'); } catch (_) { /* ignore */ }
        renameTab(tab);
    };
    // 접힌 그룹 복원
    const restoreFolds = () => {
        const root = document.querySelector('#projHub, #calList'); if (!root) return;
        let set = []; try { set = JSON.parse(localStorage.getItem('calViewFold:' + (root.dataset.viewId || '')) || '[]'); } catch (_) { set = []; }
        set.forEach((key) => { const head = root.querySelector('.cal-group-head[data-group="' + CSS.escape(key) + '"]'); if (!head) return; head.dataset.folded = '1'; const b = head.querySelector('[data-group-fold]'); if (b) b.setAttribute('aria-expanded', 'false'); root.querySelectorAll('[data-group][data-id]').forEach((row) => { if (row.dataset.group === key) row.hidden = true; }); });
    };
    document.addEventListener('calendar:navigated', () => { closePop(); pendingRename(); restoreFolds(); });
    document.addEventListener('calendar:refreshed', restoreFolds);
    pendingRename(); restoreFolds();
}
